import { LEARNED_FOLDER_NAME } from "@/lib/ai/knowledge-base/folder-types"
import { KNOWLEDGE_BASE_HREF } from "@/lib/ai/knowledge-base/page-link"
import { formatShare } from "@/lib/analytics/format"
import type { ThreadTone } from "@/lib/conversation/thread-header"
import { formatClockTime, formatShortDate } from "@/lib/conversation/time-format"
import { utcToDateKeyTz } from "@/lib/date-utils"
import { diffLines, type DiffLine } from "@/lib/utils/text-diff"

import { MIN_SHARE_POPULATION, NO_VALUE, share } from "./metric-definitions"
import { TOPIC_CATEGORY_LABELS, questionWithKind } from "./outcome-display"
import type { TopicCategory } from "./outcomes"
import type { Learner } from "./self-learning-display"
import {
  DAY_MS,
  DRAFTING_DAYS,
  checkpointAt,
  firstRunAt,
  harmOf,
  nextCheckpoint,
  tightensRule,
  waitingClosesAt,
  type Harm,
} from "./suggestions/checkpoints"
import {
  COMPARED_VERDICTS,
  MAX_CHANGE_LINE_LENGTH,
  SUGGESTION_STATUS,
  type ChangeCounts,
  type ChangeStatus,
  type ArticleAuthor,
  type ChangeResult,
  type FindNowReport,
  type FindNowStatus,
  type Measurement,
  type PromptEditOperation,
  type PromptEditPayload,
  type SupersededReason,
  type SuggestionKind,
  type UndoneReason,
  type WaitingReason,
} from "./suggestions/schema"

/**
 * Every customer-facing word about a change the agent carried and a draft waiting for a decision, for each surface
 * that shows one. Client-safe: it reads what the ledger stored and measures nothing.
 */

export const CHANGE_STATES = ["checking", "working", "taking_back", "taken_back", "replaced"] as const
export type ChangeState = (typeof CHANGE_STATES)[number]

export const CHANGE_STATE_LABELS: Record<ChangeState, string> = {
  checking: "Checking",
  working: "Working",
  taking_back: "Taking back",
  taken_back: "Taken back",
  replaced: "Replaced",
}

export const CHANGE_STATE_TONES: Record<ChangeState, ThreadTone> = {
  checking: "info",
  working: "success",
  taking_back: "warning",
  taken_back: "warning",
  replaced: "neutral",
}

/** A change as its ledger row holds it. */
export interface StoredChange {
  kind: SuggestionKind;
  /** An instruction change's; `null` for an article. */
  operation: PromptEditOperation | null;
  /** Who wrote the section an article replaced; absent or `null` for an addition. */
  replacing?: ArticleAuthor | null;
  status: ChangeStatus;
  reason: string | null;
  appliedAutomatically: boolean;
  measureState: string | null;
  decidedAt: string;
  adoptedAt: string | null;
  result: ChangeResult | null;
}

export interface ChangeDescription {
  state: ChangeState;
  line: string;
}

export const LEARNED_FROM_TEAM = "Learned from your team"

/** The learned document an agent collects a category's articles in, as it is first named. */
export function learnedDocumentTitle(agentName: string, category: TopicCategory): string {
  return `${agentName} · ${TOPIC_CATEGORY_LABELS[category]}`
}

/** «Adds to “Shop Assistant · Orders and delivery” in Learned from conversations», or what a replacement replaces. */
export function articleDestination(
  agentName: string,
  category: TopicCategory,
  before: { title: string } | null
): string {
  return before
    ? `Replaces “${before.title}” in ${LEARNED_FOLDER_NAME}`
    : `Adds to “${learnedDocumentTitle(agentName, category)}” in ${LEARNED_FOLDER_NAME}`
}

/** The Changes list shows the newest first; each «Show older» reads up to a page more. */
export const CHANGES_FIRST_PAGE = 10
export const CHANGES_PAGE = 100

export function describeChange(change: StoredChange, timezone: string, now: Date = new Date()): ChangeDescription {
  const measurement = change.result?.measurement ?? null

  switch (change.status) {
    case SUGGESTION_STATUS.APPLIED:
      return describeApplied(change, measurement, { timezone, now })
    case SUGGESTION_STATUS.UNDONE:
      return { state: "taken_back", line: undoneLine(change, measurement) }
    case SUGGESTION_STATUS.SUPERSEDED:
      return { state: "replaced", line: supersededLine(change.reason, change.kind) }
    default: {
      const exhaustive: never = change.status
      return exhaustive
    }
  }
}

/** What the change did, and who decided it when that was a person. */
export function describeChangeMade(
  change: Pick<StoredChange, "kind" | "operation" | "replacing" | "appliedAutomatically">
): string {
  return change.appliedAutomatically ? changeAction(change) : `${changeAction(change)} · you approved this`
}

/** Undo is offered while the change is the agent's own: once the customer reworded it, the line is theirs. */
export function canUndoChange(change: { status: string; adoptedAt: string | null }): boolean {
  return change.status === SUGGESTION_STATUS.APPLIED && change.adoptedAt === null
}

/**
 * What a no or an Undo promises: an instruction's text is remembered; an article's text is too, and it holds its
 * question 90 days — for good in Full autopilot.
 */
export function refusalNote(kind: SuggestionKind, decision: "no" | "undo"): string {
  switch (kind) {
    case "prompt_edit":
      return decision === "no" ? "The agent won't suggest this again." : "The agent won't make it again."
    case "kb_addition":
      return "The agent won't add this article again, and won't suggest anything for this question for at least three months."
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

/** A History version that took a change back, by why it was taken back. */
export function undoneVersionLabel(reason: string | null): string {
  switch (reason as UndoneReason | null) {
    case "worse":
      return "Taken back automatically"
    case "tool_gone":
      return "Taken back: tool turned off"
    default:
      return "Change undone"
  }
}

export interface PooledShares {
  before: number;
  after: number;
}

/**
 * The self-service rate on the questions the agent learned, before and after its changes, pooled over every final
 * verdict that compared both sides — but a change tightening a rule, which is expected to leave more to the team, and
 * one judged on a widened baseline, which never credits a change. `null` while either side holds too few to share.
 */
export function pooledResolvedShares(results: (ChangeResult | null)[]): PooledShares | null {
  const pooled = results.flatMap((result) => {
    const sides = poolableSides(result)
    return sides ? [sides] : []
  })
  const before = sumCounts(pooled.map((sides) => sides.before))
  const after = sumCounts(pooled.map((sides) => sides.after))
  if (before.requests < MIN_SHARE_POPULATION || after.requests < MIN_SHARE_POPULATION) return null
  return { before: before.resolvedByAgent / before.requests, after: after.resolvedByAgent / after.requests }
}

/** A pending take-back's `worse` has no `verdictAt` yet: it may still be refused. */
function poolableSides(result: ChangeResult | null): ComparedSides | null {
  const measurement = result?.measurement ?? null
  if (!measurement?.verdictAt || measurement.baseline?.widened || tightensRule(result)) return null
  return comparedSides(measurement)
}

function sumCounts(counts: ChangeCounts[]): Pick<ChangeCounts, "requests" | "resolvedByAgent"> {
  return counts.reduce(
    (sum, side) => ({ requests: sum.requests + side.requests, resolvedByAgent: sum.resolvedByAgent + side.resolvedByAgent }),
    { requests: 0, resolvedByAgent: 0 }
  )
}

/** «6 improvements · 1 taken back · questions it learned: 31% → 58% resolved by the agent alone». */
export function learningTally(
  learner: Learner,
  { improvements, takenBack, learnedQuestions }: { improvements: number; takenBack: number; learnedQuestions: PooledShares | null }
): string {
  return [
    improvements === 1 ? "1 improvement" : `${improvements.toLocaleString()} improvements`,
    `${takenBack.toLocaleString()} taken back`,
    ...(learnedQuestions
      ? [
          `${QUESTIONS_LEARNED[learner]}: ${formatShare(learnedQuestions.before, NO_VALUE)} → ${formatShare(learnedQuestions.after, NO_VALUE)} resolved by the agent alone`,
        ]
      : []),
  ].join(" · ")
}

/** The weekly report's self-learning line: «Shop Assistant learned 1 thing and took back 1 change this week.» */
export function learnedThisWeek(learner: string | null, learned: number, takenBack: number): string | null {
  const who = learner ?? "Your agents"
  const things = learned === 1 ? "1 thing" : `${learned.toLocaleString()} things`
  const changes = takenBack === 1 ? "1 change" : `${takenBack.toLocaleString()} changes`
  if (learned > 0 && takenBack > 0) return `${who} learned ${things} and took back ${changes} this week.`
  if (learned > 0) return `${who} learned ${things} this week.`
  if (takenBack > 0) return `${who} took back ${changes} this week.`
  return null
}

const QUESTIONS_LEARNED: Record<Learner, string> = {
  agent: "questions it learned",
  agents: "questions they learned",
}

/** «Latest: Order status (Orders and delivery) — Look the order up before handing over», and its agent under all. */
export function latestChangeLine(
  change: { title: string; topicLabel: string; topicCategory: TopicCategory; agentName: string },
  showAgent: boolean
): string {
  const latest = `Latest: ${questionWithKind(change.topicLabel, change.topicCategory)} — ${change.title}`
  return showAgent ? `${latest} · ${change.agentName}` : latest
}

function describeApplied(
  change: StoredChange,
  measurement: Measurement | null,
  view: { timezone: string; now: Date }
): ChangeDescription {
  if (change.adoptedAt) return { state: "replaced", line: "You reworded it — it's part of your text now" }

  const verdict = measurement?.verdict ?? null
  switch (verdict) {
    case null:
      return change.measureState === "final"
        ? { state: "working", line: "Kept — its result wasn't measured" }
        : { state: "checking", line: checkingLine(change, measurement, view) }
    // Judged worse but still applied: its take-back was refused, and each weekly run tries it again.
    case "worse":
      return {
        state: "taking_back",
        line: `Answers got worse: ${harmLine(change.result, measurement)}. It couldn't be taken back automatically — edit it in ${editedIn(change.kind)}`,
      }
    case "helped":
      return { state: "working", line: `Resolved by the agent alone ${shareChange(measurement, "resolved")}` }
    case "no_change":
      return { state: "working", line: noChangeLine(change.result, measurement) }
    case "not_enough":
      return { state: "working", line: "Kept — too few conversations to tell" }
    case "no_baseline":
      return { state: "working", line: "Kept — nothing earlier to compare" }
    default: {
      const exhaustive: never = verdict
      return exhaustive
    }
  }
}

function editedIn(kind: SuggestionKind): string {
  return kind === "kb_addition" ? "the knowledge base" : "the System Prompt"
}

/**
 * Until the final checkpoint's run, or the extension's once the final one was short of requests — never a day past:
 * a checkpoint waiting for reviews, or a run cut short, is judged at the next run.
 */
function checkingLine(
  change: StoredChange,
  measurement: Measurement | null,
  { timezone, now }: { timezone: string; now: Date }
): string {
  if (measurement?.learnedWhileSilent && measurement.clockStart === null) {
    return "Checking starts when the agent answers this question"
  }
  const upcoming = nextCheckpoint(measurement?.checks.map((check) => check.checkpoint) ?? [])
  const clockStart = new Date(measurement?.clockStart ?? change.decidedAt)
  const due = checkpointAt(clockStart, upcoming === "extension" ? "extension" : "final")
  const until = firstRunAt(due > now ? due : now)
  return `Checking until ${formatShortDate(until.toISOString(), timezone)}`
}

/**
 * A tightening change is expected to leave more to the team, so it is read by who was left without anyone. A widened
 * baseline never credits a change, however far the share moved.
 */
function noChangeLine(result: ChangeResult | null, measurement: Measurement | null): string {
  if (tightensRule(result)) return `No clear change: customers left without an answer ${shareChange(measurement, "left_without")}`
  const resolved = `resolved by the agent alone ${shareChange(measurement, "resolved")}`
  return measurement?.baseline?.widened
    ? `Kept — ${resolved}, with too little before it to credit the change`
    : `No clear change: ${resolved}`
}

function undoneLine(change: StoredChange, measurement: Measurement | null): string {
  switch (change.reason as UndoneReason | null) {
    case "worse":
      return `Taken back automatically: ${harmLine(change.result, measurement)}`
    case "by_you":
      return "Undone by you"
    case "tool_gone":
      return "Taken back automatically: a tool it relies on was turned off"
    default:
      return "Undone"
  }
}

function supersededLine(reason: string | null, kind: SuggestionKind): string {
  const article = kind === "kb_addition"
  switch (reason as SupersededReason | null) {
    case "rewritten":
      return article ? "Replaced by a newer article on the same question" : "Replaced by a newer change to the same line"
    case "role_applied":
      return "Replaced when a role was applied to the agent"
    case "edited":
      return article ? "Removed with other facts in the knowledge base" : "Replaced by your edits to the instructions"
    default:
      return "Replaced"
  }
}

/** The harm the check that judged the change worse found, with both shares. */
function harmLine(result: ChangeResult | null, measurement: Measurement | null): string {
  const judged = measurement?.checks.findLast((check) => check.verdict === "worse")
  const baseline = measurement?.baseline
  if (!judged || !baseline) return "answers got worse"

  const harm: Harm = harmOf(judged.checkpoint, baseline, judged.after, tightensRule(result)) ?? "left_without"
  const change = `${formatShare(rate(baseline, harm), NO_VALUE)} → ${formatShare(rate(judged.after, harm), NO_VALUE)}`
  switch (harm) {
    case "left_without":
      return `more customers left without an answer (${change})`
    case "fewer_resolved":
      return `fewer resolved by the agent alone (${change})`
    default: {
      const exhaustive: never = harm
      return exhaustive
    }
  }
}

type Rate = "resolved" | "left_without"

function shareChange(measurement: Measurement | null, metric: Rate): string {
  const sides = comparedSides(measurement)
  if (!sides) return NO_VALUE
  return `${formatShare(rate(sides.before, metric), NO_VALUE)} → ${formatShare(rate(sides.after, metric), NO_VALUE)}`
}

function rate(counts: ChangeCounts, metric: Rate | Harm): number | null {
  return share(metric === "left_without" ? counts.leftWithout : counts.resolvedByAgent, counts.requests)
}

interface ComparedSides {
  before: ChangeCounts;
  after: ChangeCounts;
}

/** The baseline and the last checkpoint, once a verdict compared them. */
function comparedSides(measurement: Measurement | null): ComparedSides | null {
  const after = measurement?.checks.at(-1)?.after
  if (!measurement?.verdict || !COMPARED_VERDICTS.includes(measurement.verdict)) return null
  return measurement.baseline && after ? { before: measurement.baseline, after } : null
}

function changeAction({ kind, operation, replacing }: Pick<StoredChange, "kind" | "operation" | "replacing">): string {
  switch (kind) {
    case "kb_addition":
      return replacing ? "Updated an article" : "Added an article"
    case "prompt_edit":
      return operation === null ? "Changed the instructions" : LINE_ACTIONS[operation]
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

const LINE_ACTIONS: Record<PromptEditOperation, string> = {
  append: "Added a line",
  replace: "Rewrote a line",
  remove: "Removed a line",
}

/** Inline applies the drafted change on the spot; review opens its dialog first. */
export type ApplyMode = "inline" | "review"

export interface WaitingDraft {
  kind: SuggestionKind;
  /** An instruction change's; `null` for an article. */
  operation: PromptEditOperation | null;
  reason: string | null;
  /** `null` when withheld from a caller who may not read the instructions, and for an article. */
  edit: PromptEditPayload | null;
  /** The change check's own sentence, where one ran and may be read. */
  checkReason: string | null;
  /** Who wrote the section an article replaces; absent or `null` for an addition. */
  replacing?: ArticleAuthor | null;
}

export interface WaitingDescription {
  /** What applying it changes. */
  headline: string;
  /** Under a limit's headline, what the change check found it loosens. */
  found: string | null;
  /** Why it waits for a person, where the headline does not say. */
  note: string | null;
  link: { href: string; label: string } | null;
  apply: ApplyMode;
  /** The person's no: keeping the line it would change, or not adding what it would add. */
  decline: string;
}

export function describeWaiting(draft: WaitingDraft): WaitingDescription {
  const reason = draft.reason as WaitingReason | null
  return {
    headline: WAITING_ACTIONS[waitingAction(draft)].headline,
    found: reason === "limit" ? draft.checkReason : null,
    note: waitingNote(reason, draft.kind),
    link: reason === "kb_limit" || reason === "kb_off" ? { href: KNOWLEDGE_BASE_HREF, label: "Open the knowledge base" } : null,
    apply: applyMode(draft, reason),
    decline: declineLabel(draft),
  }
}

/** Keeping what a change would rewrite, or not adding what it would add. */
function declineLabel({ operation, replacing }: Pick<WaitingDraft, "operation" | "replacing">): string {
  if (replacing) return "Keep the current article"
  return operation === "replace" || operation === "remove" ? "Keep mine" : "Don't add"
}

/** The count beside «Needs your decision». */
export function waitingCount(waiting: number): string {
  return waiting === 1 ? "1 waits for you" : `${waiting.toLocaleString()} wait for you`
}

/** Under a list that cannot hold every waiting draft. */
export function waitingListed(listed: number): string {
  return `Showing the ${listed.toLocaleString()} newest. Decide on these to see the rest.`
}

/** The same count, standing alone where the list is not. */
export function waitingSummary(waiting: number): string {
  switch (waiting) {
    case 0:
      return "Nothing needs your decision"
    case 1:
      return "1 change needs your decision"
    default:
      return `${waiting.toLocaleString()} changes need your decision`
  }
}

/**
 * A weekly run's days are a UTC week, named by its Monday there — the viewer's timezone could name the day before.
 * A press's are the seven days up to it, named by the day of the press in the viewer's timezone.
 */
function draftedDays(draftedFrom: string, timezone: string): string {
  const from = Date.parse(draftedFrom)
  if (new Date(from).getUTCDay() === 1 && from % DAY_MS === 0) return `the week of ${formatShortDate(draftedFrom, "UTC")}`
  const pressed = new Date(from + DRAFTING_DAYS * DAY_MS).toISOString()
  return `in the seven days to ${formatShortDate(pressed, timezone)}`
}

/** «27 conversations not resolved · the week of Sep 7 · closes Oct 21»: a waiting draft in one line. */
export function draftMetaLine(
  draft: { sessions: number; draftedFrom: string; createdAt: string },
  timezone: string
): string {
  return [
    ...(draft.sessions > 0 ? [conversationsNotResolved(draft.sessions)] : []),
    draftedDays(draft.draftedFrom, timezone),
    `closes ${closingDate(draft.createdAt, timezone)}`,
  ].join(" · ")
}

function conversationsNotResolved(sessions: number): string {
  return sessions === 1 ? "1 conversation not resolved" : `${sessions.toLocaleString()} conversations not resolved`
}

export function closingDate(createdAt: string, timezone: string): string {
  return formatShortDate(waitingClosesAt(new Date(createdAt)).toISOString(), timezone)
}

export const FIND_NOW_LABEL = "Find improvements now"
export const FIND_NOW_NO_PERMISSION = "Only people who can manage agents can look for improvements"

/** The line beside «Find improvements now»: what a press would do, or why it can't be pressed. */
export function findNowLine(status: FindNowStatus, timezone: string, now: Date = new Date()): string {
  switch (status.state) {
    case "available":
      return "Looks at the conversations of the last seven days"
    case "running":
      return "Looking at recent conversations…"
    case "available_later":
      return availableAgain(status.availableAt, timezone, now)
    case "nothing_new":
      return "No newly reviewed conversations since the last run"
    case "not_drafted":
      return "Disabled agents, and agents with Self-learning off, get no suggestions"
    default: {
      const exhaustive: never = status
      return exhaustive
    }
  }
}

function availableAgain(at: string, timezone: string, now: Date): string {
  const clock = formatClockTime(at, timezone)
  return utcToDateKeyTz(at, timezone) === utcToDateKeyTz(now.toISOString(), timezone)
    ? `Available again at ${clock}`
    : `Available again ${formatShortDate(at, timezone)} at ${clock}`
}

/** Why a press did not start after all: a run holding the lock is someone else's, not this press. */
export function findNowRefusal(status: FindNowStatus, timezone: string, now: Date = new Date()): string {
  return status.state === "running"
    ? "Already looking at recent conversations. Try again when that finishes."
    : findNowLine(status, timezone, now)
}

/** What a press found, said once it ends. */
export function findNowOutcome(
  lastRun: NonNullable<FindNowReport["lastRun"]>
): { tone: "success" | "info" | "error"; message: string } {
  if (lastRun.found > 0) {
    // Found drafts wait for a person or were applied by the agent itself, as its mode allows.
    const found = lastRun.found === 1 ? "1 new improvement" : `${lastRun.found.toLocaleString()} new improvements`
    return { tone: "success", message: `Found ${found}` }
  }
  if (!lastRun.complete) {
    return { tone: "error", message: "Looking at recent conversations stopped early. Try again later." }
  }
  switch (lastRun.held) {
    case "pace":
      return { tone: "info", message: "Nothing new: self-learning has made as many changes by itself as a week allows" }
    case "allowance":
      return { tone: "info", message: "Nothing new: this week's allowance for finding improvements is used up" }
    case null:
      return { tone: "info", message: "Nothing new to suggest from the last seven days" }
    default: {
      const exhaustive: never = lastRun.held
      return exhaustive
    }
  }
}

/** What the weekly report says an agent asks of a person: «wants to rewrite a line you wrote». */
export function waitingAsk(draft: Pick<WaitingDraft, "kind" | "operation" | "reason" | "replacing">): string {
  return WAITING_ACTIONS[waitingAction(draft)].ask
}

type WaitingAction =
  | "add_article"
  | "update_article"
  | "rewrite_article_yours"
  | "change_instructions"
  | "add_line"
  | "rewrite_line"
  | "remove_line"
  | "rewrite_yours"
  | "remove_yours"
  | "contradict_yours"

/** One action, told as the list's headline and as the report's ask. */
const WAITING_ACTIONS: Record<WaitingAction, { headline: string; ask: string }> = {
  add_article: {
    headline: "This adds an article to the knowledge base",
    ask: "wants to add an article to the knowledge base",
  },
  update_article: {
    headline: "This updates an article in the knowledge base",
    ask: "wants to update an article in the knowledge base",
  },
  rewrite_article_yours: { headline: "This rewrites an article you wrote", ask: "wants to rewrite an article you wrote" },
  change_instructions: { headline: "This changes the agent's instructions", ask: "wants to change its instructions" },
  add_line: {
    headline: "This adds a line to the agent's instructions",
    ask: "wants to add a line to its instructions",
  },
  rewrite_line: {
    headline: "This rewrites a line of the agent's instructions",
    ask: "wants to rewrite a line of its instructions",
  },
  remove_line: {
    headline: "This removes a line from the agent's instructions",
    ask: "wants to remove a line from its instructions",
  },
  rewrite_yours: { headline: "This rewrites a line you wrote", ask: "wants to rewrite a line you wrote" },
  remove_yours: { headline: "This removes a line you wrote", ask: "wants to remove a line you wrote" },
  contradict_yours: {
    headline: "This adds a line that contradicts one you wrote",
    ask: "wants to add a line that contradicts one you wrote",
  },
}

function waitingAction({
  kind,
  operation,
  reason,
  replacing,
}: Pick<WaitingDraft, "kind" | "operation" | "reason" | "replacing">): WaitingAction {
  switch (kind) {
    case "kb_addition":
      return articleAction(replacing ?? null)
    case "prompt_edit":
      return reason === "your_text" ? yourTextAction(operation) : instructionAction(operation)
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

function articleAction(replacing: ArticleAuthor | null): WaitingAction {
  switch (replacing) {
    case null:
      return "add_article"
    case "learned":
      return "update_article"
    case "yours":
      return "rewrite_article_yours"
    default: {
      const exhaustive: never = replacing
      return exhaustive
    }
  }
}

function instructionAction(operation: PromptEditOperation | null): WaitingAction {
  switch (operation) {
    case "append":
      return "add_line"
    case "replace":
      return "rewrite_line"
    case "remove":
      return "remove_line"
    case null:
      return "change_instructions"
    default: {
      const exhaustive: never = operation
      return exhaustive
    }
  }
}

function yourTextAction(operation: PromptEditOperation | null): WaitingAction {
  switch (operation) {
    case "replace":
      return "rewrite_yours"
    case "remove":
      return "remove_yours"
    case "append":
    case null:
      return "contradict_yours"
    default: {
      const exhaustive: never = operation
      return exhaustive
    }
  }
}

function waitingNote(reason: WaitingReason | null, kind: SuggestionKind): string | null {
  switch (reason) {
    case "limit":
      return "It touches a rule like discounts, refunds, handover or personal data, so it waits for you."
    case "kb_article":
      return "New articles wait for your OK before they join the knowledge base."
    case "kb_limit":
      return "Your knowledge base is full. Remove an article you no longer need, or move to a larger plan, to add this one."
    case "kb_off":
      return "The document it belongs in is turned off in the knowledge base. Turn it on to add this article."
    case "after_rejection":
      return "You said no to an earlier change for this question."
    case "after_edit":
      return "You edited out an earlier change for this question."
    case "inherited":
      return kind === "kb_addition"
        ? "This agent uses your shared instructions, so it asks before every change."
        : "This agent uses your shared instructions; applying gives it its own copy."
    default:
      return null
  }
}

/**
 * An article and an agent without its own instructions are applied from the review, as is a long line. A full
 * knowledge base is not a dead end: the article's accept refuses while it is full and adds it once there is room.
 */
function applyMode(draft: WaitingDraft, reason: WaitingReason | null): ApplyMode {
  if (draft.kind === "kb_addition" || reason === "inherited") return "review"
  return draft.edit && isLongEdit(draft.edit) ? "review" : "inline"
}

/** Longer than a drafted line may be, or spanning lines — a paragraph from before lines. */
function isLongEdit(edit: PromptEditPayload): boolean {
  return changeLines(edit).some(({ text }) => text.includes("\n") || text.length > MAX_CHANGE_LINE_LENGTH)
}

/** A replaced section beside the one taking its place, title first, line by line. */
export function articleLines(before: ArticleText, after: ArticleText): DiffLine[] {
  return diffLines(`${before.title}\n${before.content}`, `${after.title}\n${after.content}`)
}

interface ArticleText {
  title: string;
  content: string;
}

/** An instruction change on its own: the line it rewrites or removes, then the one it writes. */
export function changeLines(edit: PromptEditPayload): DiffLine[] {
  switch (edit.operation) {
    case "append":
      return [{ type: "add", text: edit.instruction }]
    case "replace":
      return [
        { type: "remove", text: edit.target },
        { type: "add", text: edit.instruction },
      ]
    case "remove":
      return [{ type: "remove", text: edit.target }]
    default: {
      const exhaustive: never = edit
      return exhaustive
    }
  }
}
